import type { BackendCapabilities } from "../backend/contracts";
import type { VaultArtifact } from "../backend/vault-artifacts";
import { LOCAL_APP_MANIFEST_SCHEMA_VERSION, type LocalAppManifest } from "../platform/contracts";

export type LocalAppDefinition = LocalAppManifest;

export const LOCAL_APP_REGISTRY: readonly LocalAppDefinition[] = [
  {
    schemaVersion: LOCAL_APP_MANIFEST_SCHEMA_VERSION,
    id: "native-workspace",
    version: "0.1.0",
    dataSchemaVersion: 4,
    label: "Native browser workspace",
    description: "Local API, CRUD, IndexedDB, snapshots and offline shell diagnostics.",
    path: "/",
    storageNamespace: "native.workspace",
    offline: true,
    network: "offline-first",
    networkNote: "No network required after shell cache is warm.",
    license: "Primary project",
    requiredCapabilities: ["local-api", "persistent-storage", "worker-execution", "snapshots"],
    resources: [
      { id: "items", version: 1, route: "/api/items", operations: ["list", "get", "create", "update", "delete"] },
      { id: "tasks", version: 1, route: "/api/tasks", operations: ["list", "get", "create", "update", "delete"] },
    ],
  },
  {
    schemaVersion: LOCAL_APP_MANIFEST_SCHEMA_VERSION,
    id: "local-tasks",
    version: "0.1.0",
    dataSchemaVersion: 2,
    label: "Local Tasks",
    description: "An independent task resource running on the browser-local kernel.",
    path: "#tasks",
    storageNamespace: "native.tasks",
    offline: true,
    network: "offline-only",
    networkNote: "Task state is local to this browser origin.",
    license: "Primary project",
    requiredCapabilities: ["local-api", "persistent-storage", "worker-execution", "snapshots"],
    resources: [{ id: "tasks", version: 1, route: "/api/tasks", operations: ["list", "get", "create", "update", "delete"] }],
  },
  {
    schemaVersion: LOCAL_APP_MANIFEST_SCHEMA_VERSION,
    id: "vortex-vault",
    version: "2.2.1",
    dataSchemaVersion: 1,
    label: "Vortex Vault",
    description: "Portable encrypted .vault workspace for files, passwords, TOTP and Direct Share.",
    path: "/vortex-vault.html",
    storageNamespace: "vortex.vault",
    offline: true,
    network: "optional-network",
    networkNote: "Vault core is local; Direct Share may need STUN/WebRTC network.",
    license: "AGPL-3.0",
    requiredCapabilities: ["offline-shell"],
    resources: [],
  },
  {
    schemaVersion: LOCAL_APP_MANIFEST_SCHEMA_VERSION,
    id: "opaque-artifact-locker",
    version: "0.1.0",
    dataSchemaVersion: 1,
    label: "Opaque vault artifact locker",
    description: "Browser-local encrypted backup blobs with hash metadata and download/restore handoff.",
    path: "#vault-artifact-list",
    storageNamespace: "native.artifacts",
    offline: true,
    network: "offline-only",
    networkNote: "Local IndexedDB only; bounded to 128 MiB per artifact.",
    license: "Primary project",
    requiredCapabilities: ["persistent-storage"],
    resources: [{ id: "opaque-artifacts", version: 1, route: "/api/opaque-artifacts", operations: ["list", "get", "create", "delete"] }],
  },
];

export type WorkspaceManifest = {
  schemaVersion: 1;
  kind: "browser-local-workspace-manifest";
  kernelContractVersion: 1;
  generatedAt: string;
  apps: readonly LocalAppDefinition[];
  capabilities: BackendCapabilities;
  opaqueArtifacts: readonly Pick<VaultArtifact, "id" | "name" | "size" | "lastModified" | "sha256" | "importedAt">[];
};

export function buildWorkspaceManifest(
  capabilities: BackendCapabilities,
  opaqueArtifacts: readonly VaultArtifact[],
): WorkspaceManifest {
  return {
    schemaVersion: 1,
    kind: "browser-local-workspace-manifest",
    kernelContractVersion: 1,
    generatedAt: new Date().toISOString(),
    apps: LOCAL_APP_REGISTRY,
    capabilities,
    opaqueArtifacts: opaqueArtifacts.map(({ id, name, size, lastModified, sha256, importedAt }) => ({
      id,
      name,
      size,
      lastModified,
      sha256,
      importedAt,
    })),
  };
}
